import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, CheckCircle, AlertTriangle, Clock, ArrowUpRight, RefreshCw,
  Monitor, Rocket, Zap, Activity, Shield, ChevronRight, BarChart3,
  Layers, Box, Download, Settings, TrendingUp, FileBox, LayoutDashboard,
  Timer, Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AnimatedCounter } from "@/components/ui/animated-counter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import axios from 'axios';
import { useAuth, API } from "@/App";
import { usePermissions } from "@/contexts/PermissionsContext";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Chart colors
const COLORS = {
  cyan: '#00D4E4',
  lime: '#BBFF71',
  coral: '#FC5442',
  blue: '#6376F0',
  purple: '#8B5CF6',
  amber: '#F59E0B',
  emerald: '#10B981',
  rose: '#F43F5E',
};

// KPI Card Component
const KPICard = ({ label, value, subtitle, icon: Icon, accentColor = COLORS.cyan, onClick, className = "" }) => (
  <Card 
    className={`bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)] hover:bg-[rgba(229,239,250,0.3)] transition-all duration-300 ${onClick ? 'cursor-pointer' : ''} ${className}`}
    onClick={onClick}
  >
    <CardContent className="p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="alice-kpi-label text-xs font-medium uppercase tracking-wider text-white/90">{label}</span>
        {Icon && (
          <div className="alice-kpi-chip w-10 h-10 rounded-xl flex items-center justify-center" style={{ "--chip-accent": `${accentColor}20`, "--icon-accent": accentColor }}>
            <Icon className="alice-kpi-icon w-5 h-5" />
          </div>
        )}
      </div>
      <div className="alice-kpi-figure text-3xl font-bold text-white">
        {typeof value === 'number' ? <AnimatedCounter value={value} /> : value}
      </div>
      {subtitle && <div className="text-xs text-white/90 mt-1">{subtitle}</div>}
      {onClick && (
        <div className="mt-2 text-xs text-white/70 flex items-center gap-1">
          View details <ChevronRight className="w-3 h-3" />
        </div>
      )}
    </CardContent>
  </Card>
);

// Donut Chart Component
const DonutChart = ({ data, size = 120, strokeWidth = 16 }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  if (total === 0) return <div style={{ width: size, height: size }} className="flex items-center justify-center text-white/70">No data</div>;
  
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  
  let offset = 0;
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {data.map((item, index) => {
          const percentage = (item.value / total) * 100;
          const strokeDasharray = (percentage / 100) * circumference;
          const strokeDashoffset = -offset;
          offset += strokeDasharray;
          
          return (
            <circle
              key={index}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={item.color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${strokeDasharray} ${circumference}`}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-500"
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xl font-bold text-white">{total}</span>
      </div>
    </div>
  );
};

// Progress Bar Component
const ProgressBar = ({ label, value, max, color }) => {
  const percentage = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-white">{label}</span>
        <span className="text-white font-medium">{value}</span>
      </div>
      <div className="h-2 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] rounded-full overflow-hidden">
        <div 
          className="h-full rounded-full transition-all duration-500" 
          style={{ width: `${percentage}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
};

const MainDashboard = () => {
  const { getAccessToken, demoMode } = useAuth();
  // An Apps Management only tenant has no OS Patch section.
  const { canUseOSPatch, canViewOSPatchPolicies } = usePermissions();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [appsStats, setAppsStats] = useState(null);
  const [osStats, setOsStats] = useState(null);
  const [error, setError] = useState(null);
  
  // Time savings calculator state
  const [manualHoursPerPackage, setManualHoursPerPackage] = useState(4); // Default 4 hours per package

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = await getAccessToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Fetch both dashboards in parallel
      const [appsRes, osRes] = await Promise.allSettled([
        axios.get(`${API}/dashboard/stats`, { headers }),
        axios.get(`${API}/os-patch/dashboard/metrics`, { headers })
      ]);
      
      if (appsRes.status === 'fulfilled') {
        setAppsStats(appsRes.value.data);
      }
      
      if (osRes.status === 'fulfilled') {
        setOsStats(osRes.value.data);
      }
      
    } catch (err) {
      console.error('Dashboard load error:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="p-8" data-testid="main-dashboard">
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 rounded-full border-4 border-[rgba(229,239,250,0.1)] border-t-cyan-400 animate-spin mb-4" />
          <span className="text-white/90 text-lg">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  // Extract stats with fallbacks
  const apps = appsStats || {};
  const os = osStats || {};
  
  const overview = apps.overview || {};
  const deploymentStages = apps.deployment_stages || {};
  const approvals = apps.approvals || {};
  const phasedDeployments = apps.phased_deployments || {};
  const automationOpportunity = apps.automation_opportunity || {};
  
  const osMetrics = os.metrics || os || {};

  // Calculate time savings
  const automatablePackages = automationOpportunity.total_with_automation || 0;
  const AUTOMATED_MINUTES_PER_PACKAGE = 15; // 15 minutes automated
  const manualTotalHours = automatablePackages * manualHoursPerPackage;
  const automatedTotalHours = (automatablePackages * AUTOMATED_MINUTES_PER_PACKAGE) / 60;
  const hoursSaved = Math.max(0, manualTotalHours - automatedTotalHours);
  const workDaysSaved = Math.round(hoursSaved / 8 * 10) / 10; // 8-hour work days

  // Prepare chart data
  const deploymentStageData = [
    { label: 'Live', value: deploymentStages.live || 0, color: COLORS.emerald },
    { label: 'Approved', value: deploymentStages.approved || 0, color: COLORS.purple },
    { label: 'Testing', value: deploymentStages.test || 0, color: COLORS.blue },
    { label: 'Not Deployed', value: deploymentStages.not_deployed || 0, color: '#4B5563' },
  ];

  const osComplianceData = [
    { label: 'Compliant', value: osMetrics.compliant_devices || 0, color: COLORS.emerald },
    { label: 'Non-Compliant', value: (osMetrics.managed_devices || 0) - (osMetrics.compliant_devices || 0), color: COLORS.coral },
  ];

  return (
    <div className="p-8" data-testid="main-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <LayoutDashboard className="h-8 w-8 text-cyan-400" />
            Dashboard
          </h1>
          <p className="text-white/90">Combined overview of Apps Management and OS Patch Management</p>
        </div>
        <Button onClick={loadData} className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold" data-testid="refresh-dashboard-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Time Savings Section */}
      <div className="mb-10">
        <div className="flex items-center gap-2 mb-4">
          <Timer className="h-5 w-5 text-cyan-400" />
          <h2 className="text-xl font-semibold text-white">Time Savings</h2>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-white/70 cursor-help ml-1" />
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-slate-900 border-slate-700 max-w-xs">
                <p className="text-sm">Calculate your potential time savings by automating package creation with ALICE. Based on {automatablePackages} packages with automation available.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        {/* Time Savings Card - Full Width */}
        <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
              {/* Left - Slider */}
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-white/90">Hours per Manual Package</span>
                    <span className="text-lg font-bold text-white">{manualHoursPerPackage}h</span>
                  </div>
                  <Slider
                    value={[manualHoursPerPackage]}
                    onValueChange={(value) => setManualHoursPerPackage(value[0])}
                    min={1}
                    max={8}
                    step={0.5}
                    className="w-full"
                    trackClassName="bg-white/30 h-2"
                    rangeClassName="bg-cyan-400"
                    thumbClassName="bg-white border-cyan-400 border-2 h-5 w-5"
                    data-testid="manual-hours-slider"
                  />
                  <div className="flex justify-between text-xs text-white/70 mt-1">
                    <span>1 hour</span>
                    <span>8 hours</span>
                  </div>
                </div>
              </div>
              
              {/* Center - Hours Saved */}
              <div className="text-center py-3 bg-[rgba(229,239,250,0.1)] rounded-xl">
                <div className="text-5xl font-bold text-cyan-400 mb-1">
                  {Math.round(hoursSaved)}
                </div>
                <div className="text-sm text-white/90">hours saved</div>
                <div className="text-xs text-white/70 mt-1">≈ {workDaysSaved} work days</div>
              </div>
              
              {/* Right - Breakdown */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-white/90">Manual effort</span>
                  <span className="text-sm font-medium text-rose-400">{manualTotalHours}h</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-white/90">Automated effort</span>
                  <span className="text-sm font-medium text-emerald-400">{automatedTotalHours.toFixed(1)}h</span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-[rgba(229,239,250,0.1)]">
                  <span className="text-sm text-white/90">Per package</span>
                  <div className="text-right">
                    <div className="text-xs text-white/70">{manualHoursPerPackage}h → 15min</div>
                    <div className="text-xs text-emerald-400">{Math.round((1 - (15/60) / manualHoursPerPackage) * 100)}% faster</div>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  className="w-full border-[rgba(229,239,250,0.2)] text-cyan-400 hover:bg-[rgba(229,239,250,0.1)] mt-2"
                  onClick={() => navigate('/discover')}
                >
                  View Automatable Apps
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Apps Management Section */}
      <div className="mb-10">
        <div className="flex items-center gap-2 mb-4">
          <Package className="h-5 w-5 text-cyan-400" />
          <h2 className="text-xl font-semibold text-white">Apps Management</h2>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-cyan-400 hover:text-cyan-300 ml-auto"
            onClick={() => navigate('/apps/dashboard')}
          >
            View Full Dashboard <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        
        <div className="alice-kpi-row grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <KPICard 
            label="Total Packages" 
            value={overview.total_packages || 0} 
            subtitle={`${overview.success_rate || 0}% success rate`}
            icon={Package} 
            accentColor={COLORS.cyan}
            onClick={() => navigate('/packages')}
          />
          <KPICard 
            label="Deployed Live" 
            value={deploymentStages.live || 0} 
            subtitle={`${phasedDeployments.active || 0} active rollouts`}
            icon={Rocket} 
            accentColor={COLORS.emerald}
            onClick={() => navigate('/deployments')}
          />
          <KPICard 
            label="In Testing" 
            value={deploymentStages.test || 0} 
            subtitle="Awaiting approval"
            icon={Clock} 
            accentColor={COLORS.amber}
          />
          <KPICard 
            label="Ready for Live" 
            value={approvals.ready_for_live || 0} 
            subtitle="All testers approved"
            icon={CheckCircle} 
            accentColor={COLORS.purple}
          />
          <KPICard 
            label="Completed" 
            value={overview.completed || 0} 
            subtitle={`${overview.failed || 0} failed`}
            icon={Activity} 
            accentColor={COLORS.blue}
          />
        </div>

        {/* Apps Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Deployment Stages */}
          <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <Layers className="w-4 h-4 text-cyan-400" />
                Deployment Stages
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <DonutChart data={deploymentStageData} />
                <div className="space-y-2 ml-4">
                  {deploymentStageData.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-white/90">{item.label}</span>
                      <span className="text-white font-medium ml-auto">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Package Status */}
          <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <Activity className="w-4 h-4 text-emerald-400" />
                Package Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <ProgressBar 
                label="Completed" 
                value={overview.completed || 0} 
                max={overview.total_packages || 1} 
                color={COLORS.emerald} 
              />
              <ProgressBar 
                label="In Progress" 
                value={overview.in_progress || 0} 
                max={overview.total_packages || 1} 
                color={COLORS.cyan} 
              />
              <ProgressBar 
                label="Failed" 
                value={overview.failed || 0} 
                max={overview.total_packages || 1} 
                color={COLORS.coral} 
              />
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <TrendingUp className="w-4 h-4 text-purple-400" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-start text-[#006AF2] bg-[#E5EFFA] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                onClick={() => navigate('/catalog')}
              >
                <Package className="w-4 h-4 mr-2 text-cyan-400" />
                Browse App Catalog
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start text-[#006AF2] bg-[#E5EFFA] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                onClick={() => navigate('/discover')}
              >
                <BarChart3 className="w-4 h-4 mr-2 text-amber-400" />
                View Rationalisation
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start text-[#006AF2] bg-[#E5EFFA] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                onClick={() => navigate('/deployments')}
              >
                <Rocket className="w-4 h-4 mr-2 text-emerald-400" />
                Manage Deployments
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* OS Patch Management Section (hidden when the tenant's plan has none) */}
      {canUseOSPatch && (
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Monitor className="h-5 w-5 text-lime-400" />
          <h2 className="text-xl font-semibold text-white">OS Patch Management</h2>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-lime-400 hover:text-lime-300 ml-auto"
            onClick={() => navigate('/os-patch/dashboard')}
          >
            View Full Dashboard <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {/* Pending Updates and Update Rings lead to the update-policy pages,
            which the reporting-only plan doesn't include, so they're hidden. */}
        <div className={`alice-kpi-row grid grid-cols-1 md:grid-cols-2 ${canViewOSPatchPolicies ? 'lg:grid-cols-5' : 'lg:grid-cols-3'} gap-4 mb-6`}>
          <KPICard
            label="Managed Devices"
            value={osMetrics.managed_devices || 0} 
            // Every managed OS is counted here; the list it opens (Discover >
            // Devices) is Windows-only, so the subtitle says how many of these
            // that list will show.
            subtitle={osMetrics.windows_managed_devices != null
              ? `${osMetrics.windows_managed_devices} Windows · ${osMetrics.online_devices || 0} online`
              : `${osMetrics.online_devices || 0} online`}
            icon={Monitor}
            accentColor={COLORS.blue}
            onClick={() => navigate('/os-patch/discover?tab=devices')}
          />
          <KPICard 
            label="Compliance Rate" 
            value={`${osMetrics.compliance_rate || 0}%`} 
            subtitle={`${osMetrics.compliant_devices || 0} compliant`}
            icon={Shield} 
            accentColor={COLORS.emerald}
          />
          <KPICard 
            label="Up to Date" 
            value={`${osMetrics.up_to_date_pct || 0}%`} 
            subtitle={`${osMetrics.up_to_date_count || 0} devices`}
            icon={CheckCircle} 
            accentColor={COLORS.lime}
          />
          {canViewOSPatchPolicies && (
            <>
              <KPICard
                label="Pending Updates"
                value={osMetrics.pending_updates || 0}
                subtitle="Devices need updates"
                icon={Download}
                accentColor={COLORS.amber}
                onClick={() => navigate('/os-patch/updates')}
              />
              <KPICard
                label="Update Rings"
                value={osMetrics.total_rings || 0}
                subtitle="Configured"
                icon={Settings}
                accentColor={COLORS.purple}
                onClick={() => navigate('/os-patch/groups')}
              />
            </>
          )}
        </div>

        {/* OS Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Device Compliance */}
          <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <Shield className="w-4 h-4 text-green-400" />
                Device Compliance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <DonutChart data={osComplianceData} />
                <div className="space-y-2 ml-4">
                  {osComplianceData.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-white/90">{item.label}</span>
                      <span className="text-white font-medium ml-auto">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Update Status */}
          <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <Download className="w-4 h-4 text-cyan-400" />
                Update Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <ProgressBar 
                label="Up to Date" 
                value={osMetrics.up_to_date_count || 0} 
                max={osMetrics.managed_devices || 1} 
                color={COLORS.emerald} 
              />
              <ProgressBar 
                label="In Progress" 
                value={osMetrics.in_progress_count || 0} 
                max={osMetrics.managed_devices || 1} 
                color={COLORS.cyan} 
              />
              <ProgressBar 
                label="Pending" 
                value={osMetrics.pending_updates || 0} 
                max={osMetrics.managed_devices || 1} 
                color={COLORS.amber} 
              />
            </CardContent>
          </Card>

          {/* OS Quick Actions */}
          <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <Zap className="w-4 h-4 text-amber-400" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-start text-[#006AF2] bg-[#E5EFFA] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                onClick={() => navigate('/os-patch/discover?tab=devices')}
              >
                <Monitor className="w-4 h-4 mr-2 text-blue-400" />
                View Windows Devices
              </Button>
              {canViewOSPatchPolicies && (
              <Button
                variant="outline"
                className="w-full justify-start text-[#006AF2] bg-[#E5EFFA] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                onClick={() => navigate('/os-patch/updates')}
              >
                <Download className="w-4 h-4 mr-2 text-cyan-400" />
                Manage Updates
              </Button>
              )}
              <Button 
                variant="outline" 
                className="w-full justify-start text-[#006AF2] bg-[#E5EFFA] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                onClick={() => navigate('/os-patch/discover')}
              >
                <BarChart3 className="w-4 h-4 mr-2 text-purple-400" />
                View Discover
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
      )}
    </div>
  );
};

export default MainDashboard;
